import React from 'react';

import { makeId, plural } from '@reckon-web/utils';
import { Flex } from '@reckon-web/flex';
import { Text } from '@reckon-web/text';
import { LoadingDots } from '@reckon-web/loading';
import { Button } from '@reckon-web/button';
import { PlusIcon } from '@reckon-web/icon';
import { SpotAttention, SpotNoResults } from '@reckon-web/illustration';
import { Heading } from '@reckon-web/heading';
import { Divider } from '@reckon-web/divider';

import { dateCompare, maybeify, stringCompare } from '../../../utils/sort';
import {
  TaskWithJob,
  useQueryTasksWithJob,
} from '../../../hooks/use-query-tasks-with-job';
import { TaskStatusBadges } from '../../common/task-status-badge';
import { KanbanOrList } from '../kanban-or-list';
import { SortType } from '../sort';
import { CreateTaskOptions } from '../../task/create-drawer';
import { Loader } from '../../common/loader';
import { ErrorScreen } from '../../common/error-screen';
import { Task } from '../../../hooks/use-query-tasks';

import { KanbanView } from './kanban-view';
import { ListView } from './list-view';

export const NoTasks: React.FC<{
  onClickCreateTask?: (options?: CreateTaskOptions) => void;
  testId?: string;
}> = ({ onClickCreateTask, testId = `no-tasks` }) => (
  <Flex
    direction="column"
    alignItems="center"
    justifyContent="center"
    gap="large"
    flexGrow={1}
    background="muted"
    borderRadius="medium"
    marginBottom="xxlarge"
    paddingBottom="xxlarge"
    data={{ testid: testId }}
  >
    <SpotAttention />
    <Heading level="3">You don't have any tasks yet</Heading>
    {onClickCreateTask && (
      <Button
        label="New task"
        iconBefore={PlusIcon}
        onClick={() => onClickCreateTask()}
        data-testid={makeId(testId, 'new-task')}
      />
    )}
  </Flex>
);

export const NoTasksFound: React.FC<{
  clearFilters?: () => void;
  testId?: string;
}> = ({ clearFilters, testId = `no-tasks-found` }) => (
  <Flex
    direction="column"
    alignItems="center"
    justifyContent="center"
    gap="large"
    flexGrow={1}
    background="muted"
    borderRadius="medium"
    marginBottom="xxlarge"
    paddingBottom="xxlarge"
    data={{ testid: testId }}
  >
    <SpotNoResults />
    <Heading level="3">No tasks found</Heading>
    <Text color="muted">
      There are no tasks matching your queries. Please refine your filters.
    </Text>
    <Button
      weight="subtle"
      tone="active"
      label="Clear filters"
      onClick={clearFilters}
      data-testid={makeId(testId, 'clear-filters')}
    />
  </Flex>
);

const compareByDueAsc = (a: TaskWithJob, b: TaskWithJob) =>
  maybeify(dateCompare)(a.dueDate, b.dueDate);
const compareByDueDesc = (a: TaskWithJob, b: TaskWithJob) =>
  maybeify(dateCompare, false)(a.dueDate, b.dueDate);
const compareByNameAsc = (a: TaskWithJob, b: TaskWithJob) =>
  maybeify(stringCompare)(a.name, b.name);
const compareByNameDesc = (a: TaskWithJob, b: TaskWithJob) =>
  maybeify(stringCompare, false)(a.name, b.name);
const compareByJobNameAsc = (a: TaskWithJob, b: TaskWithJob) =>
  maybeify(stringCompare)(a.job?.name, b.job?.name);
const compareByJobNameDesc = (a: TaskWithJob, b: TaskWithJob) =>
  maybeify(stringCompare, false)(a.job?.name, b.job?.name);
// TODO consider a sort by job id and task ID to make sure it is ABSOLUTELY always predictably the same. Or by created/updated date
const comparersBySortType: Record<
  SortType,
  ((a: TaskWithJob, b: TaskWithJob) => number)[]
> = {
  'Due Date (earliest first)': [
    // First by due date of the task
    compareByDueAsc,
    // Then by job name as the tie breaker.
    // This puts the groups in a nice visual order.
    // The group with the earliest task due date will still be first,
    // but if there are two groups with the same earliest due date, we want the job name as the tiebreaker,
    // not the names of the tasks
    compareByJobNameAsc,
    // then by task name, to get the tasks within a group to be in order
    compareByNameAsc,
  ],
  'Due Date (latest first)': [
    compareByDueDesc,
    compareByJobNameDesc,
    compareByNameDesc,
  ],
  'Name (A → Z)': [
    // first by job name, to put the groups in order
    compareByJobNameAsc,
    // then by task name, to put the tasks in the group in order
    compareByNameAsc,
    // then by due date as the final tie breaker.
    // Unlikely that a job will have two tasks with the same name. But, just in case
    compareByDueAsc,
  ],
  'Name (Z → A)': [compareByJobNameDesc, compareByNameDesc, compareByDueDesc],
};

export const Tasks: React.FC<{
  kanbanOrList: KanbanOrList;
  queryResult: ReturnType<typeof useQueryTasksWithJob>;
  filtered: boolean | string;
  clearFilter: () => void;
  sort: SortType;
  onClickJob?: (jobId: string) => void;
  onClickTask?: (taskId: string) => void;
  onClickCreateTask?: (options?: CreateTaskOptions) => void;
  testId?: string;
}> = ({
  kanbanOrList,
  queryResult,
  filtered: _filtered,
  clearFilter,
  sort,
  onClickJob,
  onClickTask,
  onClickCreateTask,
  testId = `tasks`,
}) => {
  const compare = React.useCallback(
    (a: TaskWithJob, b: TaskWithJob) => {
      const comparers = comparersBySortType[sort];
      for (const comparer of comparers) {
        const result = comparer(a, b);
        if (result !== 0) {
          return result;
        }
      }
      // got all the way to here, they must be equal
      return 0;
    },
    [sort]
  );

  const { loading, error, data } = queryResult;
  // this little dance here is so that we can render the
  // previous tasks while we fetch the next tasks
  // otherwise, as soon as the query executes
  // the data gets set to undefined (or an empty array)
  // and we start showing the Big Loader, which was undesired
  const [state, setState] = React.useState({
    data,
    filtered: _filtered,
  });
  React.useEffect(() => {
    if (data) {
      setState({ data, filtered: _filtered });
    }
  }, [data, _filtered]);

  if (error) return <ErrorScreen />;

  const { data: tasks, filtered } = state;

  return (
    <Flex direction="column" height="100%" data={{ testid: testId }}>
      <Flex alignItems="center" gap="small" paddingY="medium" flexShrink={0}>
        {tasks && (
          <Text
            size="xsmall"
            color="muted"
            data={{ testid: makeId(testId, 'showing') }}
          >
            Showing {plural(tasks.length, 'task', 'tasks')}
            {filtered && (
              <Text
                inline
                size="xsmall"
                color="dim"
                weight="medium"
                data={{ testid: makeId(testId, 'filtered') }}
              >
                &nbsp;
                {typeof filtered === 'string'
                  ? `(filtered - ${filtered})`
                  : '(filtered)'}
              </Text>
            )}
          </Text>
        )}
        {tasks && loading && (
          <LoadingDots
            label="loading"
            size="small"
            data-testid={makeId(testId, 'loading-dots')}
          />
        )}
        <Flex flexGrow={1} />
        {kanbanOrList === 'list' && tasks && (
          <TaskStatusBadges
            tasks={tasks}
            testId={makeId(testId, 'status-badge')}
          />
        )}
        {onClickCreateTask && (
          <Button
            label="New task"
            iconBefore={PlusIcon}
            size="small"
            weight="subtle"
            onClick={() => onClickCreateTask()}
            data-testid={makeId(testId, 'new-task')}
          />
        )}
      </Flex>
      {(() => {
        if (!tasks)
          return (
            <Flex direction="column" marginBottom="large" flexGrow={1}>
              <Loader
                size="large"
                label="Loading tasks"
                data-testid={makeId(testId, 'loader')}
              />
            </Flex>
          );

        if (tasks.length > 0)
          return (
            <React.Fragment>
              {kanbanOrList === 'list' && <Divider />}
              <Flex
                direction="column"
                flexGrow={1}
                marginBottom="large"
                overflow="auto"
              >
                {kanbanOrList === 'kanban' && (
                  <KanbanView
                    tasks={tasks as Task[]}
                    compare={compare}
                    onClickJob={onClickJob}
                    onClickTask={onClickTask}
                    onClickCreateTask={onClickCreateTask}
                    testId={makeId(testId, 'kanban-view')}
                  />
                )}
                {kanbanOrList === 'list' && (
                  <ListView
                    tasks={tasks as Task[]}
                    testId={makeId(testId, 'list-view')}
                  />
                )}
              </Flex>
            </React.Fragment>
          );

        if (filtered)
          return (
            <NoTasksFound
              clearFilters={clearFilter}
              testId={makeId(testId, 'no-tasks-found')}
            />
          );

        return (
          <NoTasks
            onClickCreateTask={onClickCreateTask}
            testId={makeId(testId, 'no-tasks')}
          />
        );
      })()}
    </Flex>
  );
};
